import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Receipt, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { useData } from "@/context/DataContext";
import OrderFormDialog from "@/components/dialogs/OrderFormDialog";
import ConfirmDialog from "@/components/dialogs/ConfirmDialog";
import { toast } from "sonner";

export default function Orders() {
  const { orders, clients, addOrder, updateOrder, deleteOrder } = useData();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setCreateOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const clientOptions = clients.map((c) => c.name);
  const counts = useMemo(() => {
    const c = { "In Progress": 0, Completed: 0, "Pending Payment": 0 };
    orders.forEach((o) => {
      if (c[o.status] !== undefined) c[o.status] += 1;
    });
    return c;
  }, [orders]);

  const handleCreate = (form) => {
    addOrder(form);
    toast.success("Order created");
  };
  const handleEdit = (form) => {
    updateOrder(editTarget.id, form);
    setEditTarget(null);
    toast.success("Order updated");
  };
  const handleDelete = () => {
    deleteOrder(deleteTarget.id);
    setDeleteTarget(null);
    toast.success("Order deleted");
  };

  return (
    <div className="space-y-8" data-testid="orders-page">
      <PageHeader
        title="Orders & Invoices"
        subtitle="Track your orders, payments, and service progress."
        actions={
          <Button
            data-testid="new-order-button"
            onClick={() => setCreateOpen(true)}
            className="bg-primary hover:bg-primary/90 rounded-lg btn-premium"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Order
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 stagger">
        <Card style={{ ["--i"]: 0 }} className="p-6 border border-slate-200 dark:border-slate-800 rounded-xl elev-1 tilt">
          <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.15em]">In Progress</div>
          <div className="mt-2 font-display text-3xl font-bold text-slate-900 dark:text-slate-100">{counts["In Progress"]}</div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Currently being serviced</p>
        </Card>
        <Card style={{ ["--i"]: 1 }} className="p-6 border border-slate-200 dark:border-slate-800 rounded-xl elev-1 tilt">
          <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.15em]">Completed</div>
          <div className="mt-2 font-display text-3xl font-bold text-slate-900 dark:text-slate-100">{counts["Completed"]}</div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Fully delivered</p>
        </Card>
        <Card style={{ ["--i"]: 2 }} className="p-6 border border-slate-200 dark:border-slate-800 rounded-xl elev-1 tilt">
          <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.15em]">Pending Payment</div>
          <div className="mt-2 font-display text-3xl font-bold text-slate-900 dark:text-slate-100">{counts["Pending Payment"]}</div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Awaiting invoice</p>
        </Card>
      </div>

      <Card className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden elev-1">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2">
          <Receipt className="h-4 w-4 text-slate-500 dark:text-slate-400" />
          <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">
            Order list
          </span>
        </div>
        <Table data-testid="orders-table">
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-900/60 dark:bg-slate-900/60 hover:bg-slate-50 dark:hover:bg-slate-800/60 dark:bg-slate-900/60 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800">
              <TableHead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400 font-semibold">Order</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400 font-semibold hidden md:table-cell">Client</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400 font-semibold hidden md:table-cell">Amount</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400 font-semibold hidden md:table-cell">Date</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400 font-semibold">Status</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={o.id} data-testid={`order-row-${o.id}`} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/60 dark:bg-slate-900/60 dark:bg-slate-900/60">
                <TableCell>
                  <div className="font-medium text-slate-900 dark:text-slate-100">Order {o.number}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 md:hidden">{o.client} · {o.amount}</div>
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm text-slate-600 dark:text-slate-300">{o.client}</TableCell>
                <TableCell className="hidden md:table-cell text-sm font-medium text-slate-900 dark:text-slate-100">{o.amount}</TableCell>
                <TableCell className="hidden md:table-cell text-sm text-slate-500 dark:text-slate-400">{o.date}</TableCell>
                <TableCell>
                  <StatusBadge status={o.status} testid={`order-status-${o.id}`} />
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`order-menu-${o.id}`}
                        className="h-8 w-8 text-slate-400 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white dark:text-slate-100"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        data-testid={`order-edit-${o.id}`}
                        onClick={() => setEditTarget(o)}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        data-testid={`order-delete-${o.id}`}
                        onClick={() => setDeleteTarget(o)}
                        className="text-red-600 dark:text-red-400 focus:text-red-600 dark:text-red-400"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <OrderFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreate}
        clientOptions={clientOptions}
      />
      <OrderFormDialog
        open={!!editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
        onSubmit={handleEdit}
        initial={editTarget}
        clientOptions={clientOptions}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title={`Delete Order ${deleteTarget?.number || ""}?`}
        description="This order will be removed from the demo session."
        onConfirm={handleDelete}
        testid="delete-order-confirm"
      />
    </div>
  );
}
